import React from "react";
import styled from "styled-components";
import { Dialog } from "@blueprintjs/core";
import Page from "../../components/Page";
import Async from "../../components/Async";
import { Button } from "../../components/buttons";
import api from "../../services/api";
import toasts from "../../services/toasts";
import RouteForm from "./RouteForm";
import RouteItem from "./RouteItem";

const PageActions = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
`;

const EmptyState = styled.div`
  padding: 24px 0;
  opacity: 0.75;
`;

const loadRunningRoutes = () => api.running_routes.list();

const getErrorMessage = error => {
  if (error && error.response && error.response.data) {
    return error.response.data.message || error.toString();
  }
  return error.toString();
};

class RunningRoutes extends React.Component {
  state = {
    dialogOpen: false,
    reload: false
  };

  openDialog = () => this.setState({ dialogOpen: true });
  closeDialog = () => this.setState({ dialogOpen: false });

  refresh = () => this.setState(state => ({ reload: !state.reload }));

  saveHeroImageIfNeeded = async (routeId, file) => {
    if (!file) {
      return;
    }
    await api.running_routes.uploadHeroImage({ route_id: routeId, file });
  };

  createRunningRoute = async fields => {
    const { file, ...routeFields } = fields;
    let created = null;
    try {
      created = await api.running_routes.create(routeFields);
    } catch (error) {
      toasts.error(getErrorMessage(error));
      return;
    }

    try {
      await this.saveHeroImageIfNeeded(created.id, file);
      toasts.success("Running route created");
    } catch (error) {
      toasts.error(
        `Running route created, but image upload failed: ${getErrorMessage(error)}`
      );
    }

    this.closeDialog();
    this.refresh();
  };

  updateRunningRoute = async fields => {
    const { route_id, file, ...routeFields } = fields;
    try {
      await api.running_routes.update({ route_id, ...routeFields });
    } catch (error) {
      toasts.error(getErrorMessage(error));
      return;
    }

    try {
      await this.saveHeroImageIfNeeded(route_id, file);
      toasts.success("Running route updated");
    } catch (error) {
      toasts.error(
        `Running route updated, but image upload failed: ${getErrorMessage(error)}`
      );
    }

    this.refresh();
  };

  deleteRunningRoute = async routeId => {
    try {
      await api.running_routes.delete({ route_id: routeId });
      toasts.success("Running route deleted");
      this.refresh();
    } catch (error) {
      toasts.error(getErrorMessage(error));
    }
  };

  renderRoutes = routes => {
    if (!routes.length) {
      return (
        <EmptyState>
          No running routes yet. Create one by pasting GPX or KML.
        </EmptyState>
      );
    }

    return routes.map(route => (
      <RouteItem
        key={route.id}
        route={route}
        updateRunningRoute={this.updateRunningRoute}
        deleteRunningRoute={this.deleteRunningRoute}
      />
    ));
  };

  render() {
    const { dialogOpen, reload } = this.state;

    return (
      <Page title="Running Routes">
        <PageActions>
          <Button onClick={this.openDialog}>Create Running Route</Button>
        </PageActions>

        <Async fetchProps={loadRunningRoutes} reload={reload}>
          {this.renderRoutes}
        </Async>

        <Dialog
          title="New Running Route"
          isOpen={dialogOpen}
          onClose={this.closeDialog}
        >
          <div style={{ padding: "20px 20px 0" }}>
            <RouteForm
              onCancel={this.closeDialog}
              onSubmit={this.createRunningRoute}
            />
          </div>
        </Dialog>
      </Page>
    );
  }
}

export default RunningRoutes;
